import type {
  CreateFingerprintInferenceBatchRequest,
  FileMetadata,
} from '@cnapse/shared/schemas.js';
import type * as dt from '@cnapse/shared/data.types.js';
import type { UploadedFile } from '../components/ui/FileUpload';

/** Canonical S3 object keys for fingerprint inference (deduped, order preserved). */
export function uniqueObjectKeysFromFiles(files: UploadedFile[]): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const file of files) {
    const key = file.metadata?.objectKey?.trim();
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

/** Server image-history rows plus unsaved local/temp uploads not yet on the wafer. */
export function mergeServerAndPendingFingerprintFiles(
  serverImages: FileMetadata[],
  localFiles: UploadedFile[],
): UploadedFile[] {
  const serverKeys = new Set(serverImages.map((img) => img.objectKey));
  const serverFiles: UploadedFile[] = serverImages.map((meta) => ({
    url: '',
    metadata: meta,
  }));
  const pendingOnly = localFiles.filter((file) => {
    const key = file.metadata?.objectKey;
    if (file.metadata?.fromTempFile) {
      return true;
    }
    return Boolean(key && !serverKeys.has(key));
  });
  return [...serverFiles, ...pendingOnly];
}

export function buildFingerprintInferenceBatchRequest(
  files: UploadedFile[],
  context: dt.FINGERPRINT_INFERENCE_BATCH_CONTEXT,
  options?: { waferId?: dt.IntegerId },
): CreateFingerprintInferenceBatchRequest {
  const objectKeys = uniqueObjectKeysFromFiles(files);
  if (objectKeys.length === 0) {
    throw new Error('No fingerprint images available for analysis');
  }
  return {
    objectKeys,
    context,
    ...(options?.waferId != null ? { waferId: options.waferId } : {}),
  };
}
